import { useState } from 'react';
import moment, { Moment } from 'moment';

import 'moment/locale/de';

import { DayPickerRangeController, FocusedInputShape } from 'react-dates';
import { useTranslation } from 'react-i18next';
import { ISearchBy, ISearchByOpts } from '../interfaces/search';

interface IDatePickerProps {
  activeModule?: string | null;
  searchBy: ISearchBy;
  onSetSearchBy: (searchBy: ISearchByOpts) => void;
  onChangeModule: (moduleName: string | null) => void;
  numOfMonths?: number;
  allowedDates?: { startDate: Moment; endDate: Moment };
  onApply?: () => void;
  /** Exact/flexible mode toggle — useful for search, not for proposing dates. */
  showFlexibleMode?: boolean;
}

export function DatePicker({
  searchBy,
  onSetSearchBy,
  activeModule = 'startDate',
  onChangeModule,
  numOfMonths = 2,
  allowedDates,
  onApply,
  showFlexibleMode = true,
}: IDatePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('de') ? 'de' : 'en';
  moment.locale(locale);
  const [mode, setMode] = useState<'exact' | 'flexible'>(() => {
    if (!showFlexibleMode) {
      return 'exact';
    }
    return searchBy.startDate || searchBy.endDate ? 'exact' : 'flexible';
  });

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
  }): void => {
    onSetSearchBy({ startDate: startDate?.toDate() ?? null, endDate: endDate?.toDate() ?? null });
    if (mode === 'flexible') {
      setMode('exact');
    }
  };

  const handleFocusChange = (newFocus: FocusedInputShape | null) => {
    onChangeModule(newFocus);
  };

  const clearDates = () => {
    onSetSearchBy({ startDate: null, endDate: null });
    onChangeModule('startDate');
    if (showFlexibleMode) {
      setMode('flexible');
    }
  };

  const isOutsideRange = (day: Moment) => {
    const today = moment().startOf('day');
    const dayStart = day.clone().startOf('day');

    if (dayStart.isBefore(today)) {
      return true;
    }

    if (allowedDates) {
      return (
        dayStart.isBefore(allowedDates.startDate.clone().startOf('day')) ||
        dayStart.isAfter(allowedDates.endDate.clone().endOf('day'))
      );
    }

    return false;
  };

  const nights =
    searchBy.startDate && searchBy.endDate
      ? Math.max(moment(searchBy.endDate).diff(moment(searchBy.startDate), 'days'), 0)
      : 0;

  const summary =
    searchBy.startDate && searchBy.endDate
      ? t('date-picker-summary', {
          start: moment(searchBy.startDate)
            .locale(locale)
            .format(locale === 'de' ? 'D. MMMM' : 'MMMM D'),
          end: moment(searchBy.endDate)
            .locale(locale)
            .format(locale === 'de' ? 'D. MMMM' : 'MMMM D'),
          count: nights,
        })
      : t('date-picker-summary-empty');

  return (
    <section className="datepicker notranslate" translate="no">
      <div
        className={`datepicker__toolbar${showFlexibleMode ? '' : ' datepicker__toolbar--clear-only'}`}
      >
        {showFlexibleMode && (
          <div className="datepicker__modes">
            <button
              type="button"
              className={mode === 'exact' ? 'is-active' : ''}
              onClick={() => setMode('exact')}
            >
              {t('date-picker-mode-exact')}
            </button>
            <button
              type="button"
              className={mode === 'flexible' ? 'is-active' : ''}
              onClick={() => {
                setMode('flexible');
                clearDates();
              }}
            >
              {t('date-picker-mode-flexible')}
            </button>
          </div>
        )}
        <button type="button" className="datepicker__clear" onClick={clearDates}>
          {t('date-picker-clear-dates')}
        </button>
      </div>

      <DayPickerRangeController
        startDate={searchBy.startDate ? moment(searchBy.startDate) : null}
        endDate={searchBy.endDate ? moment(searchBy.endDate) : null}
        onDatesChange={handleDatesChange}
        focusedInput={(activeModule as FocusedInputShape) || 'startDate'}
        onFocusChange={handleFocusChange}
        numberOfMonths={numOfMonths}
        minimumNights={1}
        keepOpenOnDateSelect
        noBorder
        hideKeyboardShortcutsPanel
        initialVisibleMonth={() =>
          allowedDates?.startDate ? allowedDates.startDate.clone() : moment()
        }
        isOutsideRange={isOutsideRange}
      />

      <div className="datepicker__footer">
        <span className="datepicker__summary">{summary}</span>
        {onApply && (
          <button type="button" className="datepicker__apply" onClick={onApply}>
            {t('date-picker-apply')}
          </button>
        )}
      </div>
    </section>
  );
}
